// Offline storage utilities using IndexedDB

const DB_NAME = 'TrustedBoaterOffline';
const DB_VERSION = 1;

let db = null;

export async function initOfflineDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };
    
    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      
      // Store for offline checklists
      if (!database.objectStoreNames.contains('checklists')) {
        const checklistStore = database.createObjectStore('checklists', { keyPath: 'checklist_id' });
        checklistStore.createIndex('user_id', 'user_id', { unique: false });
        checklistStore.createIndex('synced', 'synced', { unique: false });
      }
      
      // Store for offline vessels
      if (!database.objectStoreNames.contains('vessels')) {
        const vesselStore = database.createObjectStore('vessels', { keyPath: 'vessel_id' });
        vesselStore.createIndex('user_id', 'user_id', { unique: false });
      }
      
      // Store for pending sync operations
      if (!database.objectStoreNames.contains('pendingSync')) {
        database.createObjectStore('pendingSync', { keyPath: 'id', autoIncrement: true });
      }
      
      // Store for cached photos (base64)
      if (!database.objectStoreNames.contains('photos')) {
        const photoStore = database.createObjectStore('photos', { keyPath: 'photo_id' });
        photoStore.createIndex('checklist_id', 'checklist_id', { unique: false });
      }
    };
  });
}

export async function getDB() {
  if (!db) {
    await initOfflineDB();
  }
  return db;
}

// Checklist operations
export async function saveChecklistOffline(checklist) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['checklists'], 'readwrite');
    const store = transaction.objectStore('checklists');
    const request = store.put({ ...checklist, synced: false, offline_at: Date.now() });
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function getOfflineChecklists() {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['checklists'], 'readonly');
    const store = transaction.objectStore('checklists');
    const request = store.getAll();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function getUnsyncedChecklists() {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['checklists'], 'readonly');
    const store = transaction.objectStore('checklists');
    const index = store.index('synced');
    const request = index.getAll(IDBKeyRange.only(false));
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function markChecklistSynced(checklistId) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['checklists'], 'readwrite');
    const store = transaction.objectStore('checklists');
    const getRequest = store.get(checklistId);
    
    getRequest.onsuccess = () => {
      const checklist = getRequest.result;
      if (checklist) {
        checklist.synced = true;
        checklist.synced_at = Date.now();
        const putRequest = store.put(checklist);
        putRequest.onerror = () => reject(putRequest.error);
        putRequest.onsuccess = () => resolve(putRequest.result);
      } else {
        resolve(null);
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

// Vessel operations
export async function saveVesselOffline(vessel) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['vessels'], 'readwrite');
    const store = transaction.objectStore('vessels');
    const request = store.put(vessel);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function getOfflineVessels() {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['vessels'], 'readonly');
    const store = transaction.objectStore('vessels');
    const request = store.getAll();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

// Photo operations (store base64 for offline)
export async function savePhotoOffline(photoData) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['photos'], 'readwrite');
    const store = transaction.objectStore('photos');
    const request = store.put(photoData);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function getOfflinePhotos(checklistId) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['photos'], 'readonly');
    const store = transaction.objectStore('photos');
    const index = store.index('checklist_id');
    const request = index.getAll(IDBKeyRange.only(checklistId));
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

// Pending sync operations
export async function addPendingSync(operation) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['pendingSync'], 'readwrite');
    const store = transaction.objectStore('pendingSync');
    const request = store.add({ ...operation, created_at: Date.now() });
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function getPendingSyncs() {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['pendingSync'], 'readonly');
    const store = transaction.objectStore('pendingSync');
    const request = store.getAll();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

export async function clearPendingSync(id) {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['pendingSync'], 'readwrite');
    const store = transaction.objectStore('pendingSync');
    const request = store.delete(id);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

// Check if online
export function isOnline() {
  return navigator.onLine;
}

// Network status listener
export function onNetworkChange(callback) {
  window.addEventListener('online', () => callback(true));
  window.addEventListener('offline', () => callback(false));
}
